import type {
  AgenticLogicPlacementAssignmentDto,
  AgenticLogicPlacementDto,
} from "@intelligems/ig-types/dtos/agentic-logic/placements";
import type {
  AgenticLogicRuleDeploySummaryDto,
  AgenticLogicRuleLiveSummaryDto,
} from "@intelligems/ig-types/dtos/agentic-logic/deploy-summary";
import type {
  AgenticLogicRuleDto,
  AgenticLogicRuleState,
} from "@intelligems/ig-types/dtos/agentic-logic/rules";
import type { AgenticLogicSurface } from "@intelligems/ig-types/dtos/agentic-logic/surface";
import type { AgenticLogicTemplateSummaryDto } from "@intelligems/ig-types/functions/agentic-logic/templates/list";
import type { ExperienceCategory } from "@intelligems/ig-types/dtos/experience-categories";
import {
  describeAttachment,
  MISSING_REFERENCE_LABEL,
} from "../assignments/assignmentRows";
import { visibleSurfaces } from "./surfaceLabels";
import { isAgenticLogicSystemRule } from "@intelligems/ig-types/dtos/agentic-logic/rules";
import {
  hasUnpublishedRuleConfig,
  hasUnpublishedRuleSettings,
} from "../workspace/unpublishedSettings";

/**
 * One place this rule serves, as the merchant names it: the experience and
 * variation when the assignment is variation-scoped, otherwise the placement
 * it backstops store-wide. Row identity is the ASSIGNMENT, not the placement,
 * because one placement can serve several experiences and each of them is a
 * separate answer to "where does my change land".
 */
export interface RuleListUsage {
  assignmentId: string;
  name: string;
  /** Qualifies a usage that is not one experience; null when the name is one. */
  detail: string | null;
  /** The experience this usage opens, when the assignment resolves one. */
  experience: { id: string; category: ExperienceCategory } | null;
}

export interface RuleListRow {
  rule: AgenticLogicRuleDto;
  /**
   * The rule's deploy outcome, or `null` when nothing has been deployed for it
   * yet. Carried on the row rather than derived here so the ONE derivation in
   * `ruleDisplayStatus` — which also needs the org's Advanced verdict, a thing
   * this pure builder has no business reading — stays the only place a rule's
   * state becomes something a reader sees.
   */
  deploy: AgenticLogicRuleDeploySummaryDto | null;
  /** Null means eligibility could not be supplied for this rule. */
  eligibleSurfaces: AgenticLogicSurface[] | null;
  /** Null means assignments or placements could not be loaded. */
  usedIn: RuleListUsage[] | null;
  /**
   * The rule's saved draft, settings or configuration differ from its live
   * version — the same comparison the rule page makes, from the list's
   * live-version summaries. `false` for a rule with nothing live.
   */
  hasUnappliedChanges: boolean;
}

interface BuildRuleListRowsInput {
  rules: readonly AgenticLogicRuleDto[];
  deploys?: readonly AgenticLogicRuleDeploySummaryDto[];
  templates?: readonly AgenticLogicTemplateSummaryDto[];
  placements?: readonly AgenticLogicPlacementDto[];
  assignments?: readonly AgenticLogicPlacementAssignmentDto[];
  liveVersions?: readonly AgenticLogicRuleLiveSummaryDto[];
}

export const buildRuleListRows = ({
  rules,
  deploys,
  templates,
  placements,
  assignments,
  liveVersions,
}: BuildRuleListRowsInput): RuleListRow[] => {
  const liveByRuleId = new Map(
    (liveVersions ?? []).map((live) => [live.ruleId, live])
  );
  const hasUnappliedChanges = (rule: AgenticLogicRuleDto): boolean => {
    const live = liveByRuleId.get(rule.id);
    return (
      live !== undefined &&
      (hasUnpublishedRuleSettings(rule.settings, live.settings) ||
        hasUnpublishedRuleConfig(rule.templateParams, live.templateParams) ||
        !live.draftMatches)
    );
  };
  const deployByRuleId = new Map(
    (deploys ?? []).map((deploy) => [deploy.ruleId, deploy])
  );
  const templatesById = new Map(
    (templates ?? []).map((template) => [template.id, template])
  );
  const placementsById = new Map(
    (placements ?? []).map((placement) => [placement.id, placement])
  );
  const usageByRuleId =
    placements === undefined || assignments === undefined
      ? null
      : buildUsageByRuleId(assignments, placementsById);

  // The two rule kinds answer this from different places, and neither is a
  // constant any more: a code rule declares its surfaces when it is created, a
  // builder rule inherits its template's. A code rule with no declaration is
  // one created before they existed and never backfilled; it reads as unknown
  // rather than as "everywhere".
  const eligibleSurfacesOf = (
    rule: AgenticLogicRuleDto
  ): AgenticLogicSurface[] | null => {
    if (rule.managedBy === "code") {
      if (!rule.targetSurfaces) {
        return null;
      }
      return visibleSurfaces(rule.targetSurfaces);
    }
    if (templates === undefined || !rule.templateId) {
      return null;
    }
    const template = templatesById.get(rule.templateId);
    if (!template) {
      return null;
    }
    return visibleSurfaces(template.eligibleSurfaces);
  };

  // A built-in rule is Intelligems' to manage, not the merchant's: it cannot
  // be renamed, edited, paused or deleted, so it has no row to act on. It is
  // offered where it applies instead (the Checkout > Upsell Rule menu).
  return rules
    .filter((rule) => !isAgenticLogicSystemRule(rule))
    .map((rule) => ({
      rule,
      deploy: deployByRuleId.get(rule.id) ?? null,
      eligibleSurfaces: eligibleSurfacesOf(rule),
      usedIn: usageByRuleId?.get(rule.id) ?? (usageByRuleId ? [] : null),
      hasUnappliedChanges: hasUnappliedChanges(rule),
    }))
    .sort(
      (left, right) =>
        ruleListRank(left) - ruleListRank(right) ||
        right.rule.lastUpdateTs - left.rule.lastUpdateTs
    );
};

const STATE_RANK: Record<AgenticLogicRuleState, number> = {
  active: 0,
  paused: 2,
  draft: 3,
};

/**
 * Serving rules come first, and an active rule that is actually attached to a
 * placement outranks one that is not; everything else falls back to its state.
 * Unavailable usage (`null`) cannot claim the attached tier.
 */
const ruleListRank = ({ rule, usedIn }: RuleListRow): number =>
  rule.state === "active" ? (usedIn?.length ? 0 : 1) : STATE_RANK[rule.state];

/**
 * One rule's usages, from the same join the list builds — null while either
 * read is unknown, so a read in flight never reads as "used nowhere".
 */
export const ruleUsages = ({
  ruleId,
  placements,
  assignments,
}: {
  ruleId: string;
  placements: readonly AgenticLogicPlacementDto[] | undefined;
  assignments: readonly AgenticLogicPlacementAssignmentDto[] | undefined;
}): RuleListUsage[] | null =>
  placements === undefined || assignments === undefined
    ? null
    : (buildUsageByRuleId(
        assignments.filter((assignment) => assignment.ruleId === ruleId),
        new Map(placements.map((placement) => [placement.id, placement]))
      ).get(ruleId) ?? []);

const buildUsageByRuleId = (
  assignments: readonly AgenticLogicPlacementAssignmentDto[],
  placementsById: ReadonlyMap<string, AgenticLogicPlacementDto>
): Map<string, RuleListUsage[]> => {
  const usages = new Map<string, RuleListUsage[]>();

  for (const assignment of assignments) {
    const forRule = usages.get(assignment.ruleId) ?? [];
    forRule.push(
      describeUsage(
        assignment,
        placementsById.get(assignment.placementId)?.name ??
          MISSING_REFERENCE_LABEL
      )
    );
    usages.set(assignment.ruleId, forRule);
  }

  return new Map(
    [...usages.entries()].map(([ruleId, forRule]) => [
      ruleId,
      [...forRule].sort((left, right) => left.name.localeCompare(right.name)),
    ])
  );
};

/**
 * Names a usage the way the assignments grid names the same row, so the two
 * screens never disagree about what a rule is attached to. A global row is the
 * placement's store-wide default rather than one experience, so it keeps the
 * placement name and says so instead of pretending to be an experience.
 */
const describeUsage = (
  assignment: AgenticLogicPlacementAssignmentDto,
  placementName: string
): RuleListUsage => {
  const attachment = describeAttachment(assignment);
  switch (attachment.kind) {
    case "experience":
      return {
        assignmentId: assignment.id,
        name: `${attachment.experienceName} — ${attachment.variationName}`,
        detail: null,
        experience: assignment.experience
          ? {
              id: assignment.experience.id,
              category: assignment.experience.category,
            }
          : null,
      };
    case "unresolved-variation":
      return {
        assignmentId: assignment.id,
        name: placementName,
        detail: "experiment variation no longer exists",
        experience: null,
      };
    // A row reached through an assignment is attached by construction, so
    // "none" cannot occur here and falls in with the store-wide default.
    case "global":
    case "none":
      return {
        assignmentId: assignment.id,
        name: placementName,
        detail: "all experiences",
        experience: null,
      };
  }
};
